import React, { useState } from 'react';
import { Info, Plus, RefreshCw, Settings, SlidersHorizontal, X } from 'lucide-react';
import { indicatorDict, mockStudents } from '../data/mockData';

// 我的加分项页面
// 页面结构：顶部 Tab + 加分项申请列表 + 我的申请筛选和列表
export default function MyBonusPage() {
  // ================= 页面状态 =================
  const [activeTab, setActiveTab] = useState('apply');

  return (
    <div className="animate-fade-in space-y-4">
      {/* 顶部 Tab：切换加分项申请和我的申请 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 px-5 pt-4">
        <div className="flex gap-8 border-b border-gray-100">
          <button
            onClick={() => setActiveTab('apply')}
            className={`pb-3 text-sm ${activeTab === 'apply' ? 'text-[#1890ff] border-b-2 border-[#1890ff] font-bold' : 'text-gray-600 hover:text-[#1890ff]'}`}
          >
            加分项申请
          </button>
          <button
            onClick={() => setActiveTab('mine')}
            className={`pb-3 text-sm ${activeTab === 'mine' ? 'text-[#1890ff] border-b-2 border-[#1890ff] font-bold' : 'text-gray-600 hover:text-[#1890ff]'}`}
          >
            我的申请
          </button>
        </div>
      </div>

      {activeTab === 'apply' && (
        <BonusApplyTab />
      )}

      {activeTab === 'mine' && (
        <MyApplyTab />
      )}
    </div>
  );
}

// 加分项申请 Tab
function BonusApplyTab() {
  // ================= 页面状态 =================
  const [drawerConfig, setDrawerConfig] = useState({
    isOpen: false,
    batch: null,
  });

  // ================= 加分项申请列表数据 =================
  const applyList = [
    {
      id: 1,
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季综合测评批次',
      unit: '计算机学院',
    },
    {
      id: 2,
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季综合测评批次',
      unit: '经济学院',
    },
  ];

  return (
    <>
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 加分项申请列表 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <TableHead>序号</TableHead>
                <TableHead>学年</TableHead>
                <TableHead>学期</TableHead>
                <TableHead>批次</TableHead>
                <TableHead>指标所属单位（学院）</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {applyList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <TableCell>{row.batch}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button
                      onClick={() => setDrawerConfig({
                        isOpen: true,
                        batch: row,
                      })}
                      className="text-[#1890ff] hover:underline"
                    >
                      我要申请
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 右侧抽屉：加分项申请详情页，严格按照参考 App.js 的大抽屉样式 */}
      <BonusApplyDrawer
        isOpen={drawerConfig.isOpen}
        batch={drawerConfig.batch}
        student={mockStudents[0]}
        onClose={() => setDrawerConfig({
          isOpen: false,
          batch: null,
        })}
      />
    </>
  );
}

// 学生端：加分项申请抽屉
function BonusApplyDrawer({ isOpen, batch, student, onClose }) {
  if (!isOpen || !batch || !student) return null;

  return (
    <div className={`fixed inset-0 z-[60] flex justify-end transition-opacity duration-300 ${isOpen ? 'bg-black/40 opacity-100 visible' : 'bg-transparent opacity-0 invisible pointer-events-none'}`}>
      <div className="absolute inset-0" onClick={onClose} />
      <div className={`relative w-[96vw] max-w-[1550px] bg-[#f5f7fa] h-full shadow-[-10px_0_30px_rgba(0,0,0,0.15)] flex flex-col transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* 抽屉标题栏 */}
        <div className="h-[60px] px-6 border-b border-gray-200 flex justify-between items-center bg-white shrink-0 z-20">
          <h2 className="text-[17px] font-bold text-gray-800 flex items-center">
            <div className="w-1 h-4 bg-[#1890ff] rounded-sm mr-2" />
            加分项申请 - {batch.batch}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* 抽屉内容区 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* 卡片1：学生基本信息 */}
          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-[14px] mb-4 flex items-center">
              <div className="w-1 h-3 bg-[#1890ff] mr-2" />
              学生基本信息
            </h3>
            <div className="grid grid-cols-4 gap-y-3 text-[13px] text-gray-600 pl-2">
              <div>
                <span className="text-gray-400 w-24 inline-block">姓名：</span>
                <span className="font-medium text-gray-800">{student.name}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">学号：</span>
                <span className="font-medium text-gray-800">{student.studentId}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">学院/专业：</span>
                <span className="text-gray-800">{student.college} - {student.major}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">年级/班级：</span>
                <span className="text-gray-800">{student.grade} - {student.className}</span>
              </div>
            </div>
          </div>

          {/* 卡片2：综合测评指标加分项申请 */}
          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-[14px] mb-4 flex items-center">
              <div className="w-1 h-3 bg-[#1890ff] mr-2" />
              综合测评指标加分项申请
            </h3>
            <div className="bg-blue-50 border border-blue-100 p-2.5 rounded mb-4 text-[12px] text-blue-700 flex items-start">
              <Info size={14} className="mr-1.5 shrink-0 mt-0.5" />
              <span>
                请仔细阅读各项指标的申请条件。如需参评，请勾选“是否参评”，并按照要求上传附件或填写文字说明。预计得分会自动计算（受分数和次数限制）。
              </span>
            </div>
            <StudentBonusApplyTable />
          </div>

          <div className="h-4" />
        </div>

        {/* 抽屉底部操作区 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-white z-20 shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-[14px] transition-colors font-medium"
          >
            取消
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-[14px] transition-colors font-medium"
          >
            保存草稿
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#1890ff] text-white rounded hover:bg-[#096dd9] text-[14px] shadow-sm transition-colors font-medium"
          >
            提交申请
          </button>
        </div>
      </div>
    </div>
  );
}

// 学生端申请表：同级一致化排版
function StudentBonusApplyTable() {
  // ================= 表单状态 =================
  const [formData, setFormData] = useState({});

  const formatLimit = (value) => (!value || value === '无') ? '-' : value;

  const handleParticipateChange = (itemName, checked) => {
    setFormData(prev => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        isParticipate: checked,
        count: checked ? 1 : '',
        evidence: '',
      },
    }));
  };

  const handleCountChange = (itemName, value, limitValue) => {
    let numberValue = parseInt(value, 10);
    const limit = limitValue !== '-' ? parseInt(limitValue, 10) : Infinity;

    if (Number.isNaN(numberValue) || numberValue < 1) {
      numberValue = 1;
    }

    if (numberValue > limit) {
      numberValue = limit;
    }

    setFormData(prev => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        count: numberValue,
      },
    }));
  };

  const handleEvidenceChange = (itemName, value) => {
    setFormData(prev => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        evidence: value,
      },
    }));
  };

  return (
    <div className="overflow-hidden w-full border border-gray-300 rounded-sm shadow-sm bg-white">
      <table className="w-full text-left text-[12px] border-collapse table-fixed min-w-full">
        <thead className="bg-[#f0f2f5] border-b border-gray-300 text-gray-700">
          <tr>
            <th className="border-r border-gray-300 px-1 py-2 font-bold text-center whitespace-nowrap w-[5%]">考核大类</th>
            <th className="border-r border-gray-300 px-1 py-2 font-bold text-center whitespace-nowrap w-[6%]">指标类型</th>
            <th className="border-r border-gray-300 px-2 py-2 font-bold text-center w-[11%]">一级指标</th>
            <th className="border-r border-gray-300 px-2 py-2 font-bold whitespace-nowrap w-[25%]">二级指标/细项</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">需要<br />材料</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">单次<br />得分</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">次数<br />限制</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">分数<br />限制</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">是否<br />参评</th>
            <th className="border-r border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight w-[3%] break-words">参评<br />个数</th>
            <th className="border-r border-gray-300 px-2 py-2 font-bold text-center w-[26%]">上传材料/填写说明</th>
            <th className="px-1 py-2 font-bold text-center whitespace-nowrap text-[#1890ff] w-[8%]">预计得分</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(indicatorDict).map(([categoryKey, category]) => {
            const rows = buildIndicatorApplyRows(category);
            const categoryExpectedTotal = rows.reduce((sum, item) => {
              const rowData = formData[item.name];

              if (rowData && rowData.isParticipate) {
                const score = parseFloat(item.itemScore) * (rowData.count || 1);
                const limit = item.scoreLimit !== '-' ? parseFloat(item.scoreLimit) : Infinity;
                return sum + Math.min(score, limit);
              }

              return sum;
            }, 0);

            return (
              <React.Fragment key={categoryKey}>
                {rows.map((item, index) => {
                  const isFirstOverall = index === 0;
                  const rowData = formData[item.name] || {};
                  const isParticipating = rowData.isParticipate || false;

                  return (
                    <tr
                      key={`${categoryKey}-${item.bCatName}-${item.name}`}
                      className={`border-b border-gray-300 bg-white transition-colors ${isParticipating ? 'bg-blue-50/20' : 'hover:bg-gray-50'}`}
                    >
                      {isFirstOverall && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 text-center font-bold text-gray-700 bg-[#fafafa]">
                          {category.title}
                        </td>
                      )}

                      {isFirstOverall && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 text-center text-gray-600 bg-[#fcfcfc]">
                          加分项
                          <br />
                          <span className="text-gray-400 text-[11px]">
                            (满分 {category.bonMax}分)
                          </span>
                        </td>
                      )}

                      {item.isFirstOfBCat && (
                        <td rowSpan={item.rowSpanForBCat} className="border-r border-gray-300 text-center text-gray-700 font-medium py-2 px-1.5 bg-gray-50/30 break-words">
                          {item.bCatName}
                        </td>
                      )}

                      <td className="border-r border-gray-300 px-2 py-2.5 text-gray-800 leading-snug break-words">
                        {item.name}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 break-words text-[11px]">
                        {item.needProof}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 font-bold break-words text-[11px]">
                        {item.itemScore}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 break-words text-[11px]">
                        {formatLimit(item.countLimit)}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 break-words text-[11px]">
                        {formatLimit(item.scoreLimit)}
                      </td>
                      <td className="border-r border-gray-300 text-center align-middle px-0.5">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-[#1890ff] cursor-pointer rounded border-gray-300 focus:ring-[#1890ff]"
                          checked={isParticipating}
                          onChange={(event) => handleParticipateChange(item.name, event.target.checked)}
                        />
                      </td>
                      <td className="border-r border-gray-300 text-center px-0.5 align-middle">
                        <input
                          type="number"
                          min="1"
                          disabled={!isParticipating}
                          value={rowData.count ?? ''}
                          onChange={(event) => handleCountChange(item.name, event.target.value, item.countLimit)}
                          className={`w-full max-w-[40px] px-1 py-1 text-center rounded border outline-none text-[12px] ${isParticipating ? 'border-blue-400 focus:ring-1 focus:ring-[#1890ff] bg-white text-gray-800 font-bold' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        />
                      </td>
                      <td className="border-r border-gray-300 px-2 py-1.5 align-middle break-words">
                        <ApplyMaterialCell
                          item={item}
                          rowData={rowData}
                          isParticipating={isParticipating}
                          onEvidenceChange={handleEvidenceChange}
                        />
                      </td>

                      {isFirstOverall && (
                        <td rowSpan={rows.length} className="text-center font-bold text-[#1890ff] align-middle bg-blue-50/10 text-[15px]">
                          {categoryExpectedTotal}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// 上传材料/填写说明单元格
function ApplyMaterialCell({ item, rowData, isParticipating, onEvidenceChange }) {
  if (!isParticipating) {
    return (
      <span className="text-gray-300 text-[11px] text-center block w-full">
        请先勾选参评
      </span>
    );
  }

  if (item.needProof === '无') {
    return (
      <span className="text-green-600 text-[11px] flex items-center justify-center">
        <Info size={12} className="mr-1" />
        无需证明
      </span>
    );
  }

  if (item.needProof === '附件') {
    return (
      <div className="flex items-center justify-center">
        <button className="flex items-center px-2 py-1.5 bg-white border border-gray-300 rounded text-gray-600 hover:text-[#1890ff] hover:border-[#1890ff] transition-colors text-[11px]">
          <Plus size={12} className="mr-1" />
          上传附件
        </button>
      </div>
    );
  }

  return (
    <textarea
      maxLength={item.textLimit || 100}
      value={rowData.evidence ?? ''}
      onChange={(event) => onEvidenceChange(item.name, event.target.value)}
      placeholder={`不超过${item.textLimit || 100}字...`}
      className="w-full bg-white border border-blue-300 rounded p-1.5 text-[11px] outline-none focus:border-[#1890ff] focus:ring-1 focus:ring-[#1890ff] resize-y min-h-[40px] text-gray-700"
    />
  );
}

// 将指标配置转换成申请表行，并保留一级指标合并单元格需要的行数。
function buildIndicatorApplyRows(category) {
  return category.bonusCats.flatMap(bonusCategory => (
    bonusCategory.items.map((item, index) => ({
      bCatName: bonusCategory.name,
      isFirstOfBCat: index === 0,
      rowSpanForBCat: bonusCategory.items.length,
      ...item,
    }))
  ));
}

// 我的申请 Tab
function MyApplyTab() {
  // ================= 我的申请列表数据 =================
  const myApplyList = [
    {
      id: 1,
      name: '陈红',
      studentId: '2102001',
      className: '计算机1班',
      college: '计算机学院',
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季综合测评批次',
      unit: '计算机学院',
      auditStatus: '审核中',
      currentNode: '辅导员审核',
      applyTime: '2026-05-09 14:18',
    },
    {
      id: 2,
      name: '王五',
      studentId: '2102001',
      className: '计算机1班',
      college: '计算机学院',
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季综合测评批次',
      unit: '计算机学院',
      auditStatus: '已通过',
      currentNode: '流程结束',
      applyTime: '2026-05-08 10:30',
    },
  ];

  return (
    <>
      {/* 条件筛选卡片：按学年和学期查询我的申请 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
          <FilterSelect label="学年" />
          <FilterSelect label="学期" />

          <div className="flex-1" />

          <div className="flex gap-3">
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              重置
            </button>
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 结果卡片：我的申请列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex justify-end mb-4">
          <TableToolbar />
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <TableHead>序号</TableHead>
                <TableHead>姓名</TableHead>
                <TableHead>学号</TableHead>
                <TableHead>班级</TableHead>
                <TableHead>学院</TableHead>
                <TableHead>学年</TableHead>
                <TableHead>学期</TableHead>
                <TableHead>批次</TableHead>
                <TableHead>指标所属单位</TableHead>
                <TableHead>审核状态</TableHead>
                <TableHead>当前审批环节</TableHead>
                <TableHead>申请时间</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {myApplyList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.college}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <TableCell>{row.batch}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>
                    <span className={row.auditStatus === '已通过' ? 'text-green-600' : 'text-orange-500'}>
                      {row.auditStatus}
                    </span>
                  </TableCell>
                  <TableCell>{row.currentNode}</TableCell>
                  <TableCell>{row.applyTime}</TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button className="text-[#1890ff] hover:underline">
                      查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// 条件筛选：下拉框
function FilterSelect({ label }) {
  return (
    <label className="block">
      <span className="block mb-2 text-sm text-gray-700">
        {label}
      </span>
      <select className="w-[220px] border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-700">
        <option>全部</option>
        <option>2025-2026</option>
        <option>第一学期</option>
      </select>
    </label>
  );
}

// 表格工具按钮
function TableToolbar() {
  return (
    <div className="flex items-center gap-4 text-gray-700">
      <button className="hover:text-[#1890ff]" title="刷新">
        <RefreshCw size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="密度">
        <SlidersHorizontal size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="设置">
        <Settings size={18} />
      </button>
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}
